import React, { useState, useContext, useEffect, useRef, lazy, Suspense } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Menu,
  Sun,
  Moon,
  UserCircle,
  LogOut,
  X,
  HardDrive,
  SlidersHorizontal,
  Bell,
  Megaphone,
  Volume2,
  ClipboardList,
  Calculator,
  FileText,
  DollarSign,
  CloudSun,
  Info,
  LifeBuoy,
  CircleHelp,
  PauseCircle,
} from "lucide-react";
import Tooltip from "@material-ui/core/Tooltip";
import UserStatusModal from "../components/UserStatusModal";

import defaultAvatar from "../assets/avatar.svg";
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import { AuthContext } from "../context/Auth/AuthContext";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";
import ColorModeContext from "../layout/themeContext";
import { getBackendUrl } from "../config";
import Logo from "../components/Logo/";
import IconButtonWithTooltip from "../components/ui/IconButtonWithTooltip";
import "./navbar.css";
import api from "../services/api";
import { isPlanFeatureEnabled } from "../utils/planFeatureAccess";
import useSettings from "../hooks/useSettings";
import { HEADER_CONFIG_SETTING_KEY, parseHeaderConfig } from "../utils/headerConfig";
import HeaderIcon from "../components/HeaderIcon";
import { GlobalSoftphoneProvider } from "../context/GlobalSoftphone/GlobalSoftphoneContext";
import FloatingWidget from "../components/FloatingWidget";
import { useWidgetStore } from "../store/widgetStore";

// Code-splitting de widgets e modais secundários para reduzir bundle.js inicial
const UserModal = lazy(() => import("../components/UserModal"));
const HelpAssistantModal = lazy(() => import("../components/HelpAssistantModal"));
const GlobalSoftphoneWidget = lazy(() => import("../components/GlobalSoftphone"));
const ClipboardDrawer = lazy(() => import("../components/ClipboardDrawer"));
const DollarWidget = lazy(() => import("../components/Widgets/DollarWidget"));
const CalculatorWidget = lazy(() => import("../components/Widgets/CalculatorWidget"));
const NotepadWidget = lazy(() => import("../components/Widgets/NotepadWidget"));
const WeatherWidget = lazy(() => import("../components/Widgets/WeatherWidget"));

const backendUrl = getBackendUrl();

const resolveProfileImageUrl = (companyId, image) => {
  const imageName = String(image || "").trim();

  if (!companyId || !imageName || imageName.toLowerCase().includes("nopicture")) {
    return defaultAvatar;
  }

  if (/^https?:\/\//i.test(imageName)) {
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      /^http:\/\/(localhost|127\.0\.0\.1)/i.test(imageName)
    ) {
      return defaultAvatar;
    }
    return imageName;
  }

  const base = String(backendUrl || "").replace(/\/+$/, "");
  const url = `${base}/public/company${companyId}/user/${imageName}`;

  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    /^http:\/\/(localhost|127\.0\.0\.1)/i.test(url)
  ) {
    return defaultAvatar;
  }

  return url;
};

const LoggedInLayout = ({ children }) => {
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userStatusModalOpen, setUserStatusModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const [clipboardDrawerOpen, setClipboardDrawerOpen] = useState(false);
  const { activeWidgets } = useWidgetStore();
  const { handleLogout, loading } = useContext(AuthContext);
  const { user, socket } = useContext(AuthContext);
  const { get: getSetting } = useSettings();
  const { whatsApps } = useContext(WhatsAppsContext);
  const history = useHistory();
  const location = useLocation();
  const colorModeContext = useContext(ColorModeContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [diskUsage, setDiskUsage] = useState(null);
  const [planFeatures, setPlanFeatures] = useState({});
  const [planFeaturesLoaded, setPlanFeaturesLoaded] = useState(false);
  const [headerConfig, setHeaderConfig] = useState(() => parseHeaderConfig());
  const [helpAssistantOpen, setHelpAssistantOpen] = useState(false);
  const announcementPermission = String(user?.allowAnnouncements || "").toLowerCase();
  const teamAnnouncementPermission = String(
    user?.team?.permissions?.allowAnnouncements || ""
  ).toLowerCase();
  const canAccessAnnouncements =
    user?.super === true ||
    (announcementPermission !== "disabled" &&
      !(
        (teamAnnouncementPermission === "disabled" ||
          teamAnnouncementPermission === "false") &&
        announcementPermission !== "enabled"
      ));

  useEffect(() => {
    getSetting(HEADER_CONFIG_SETTING_KEY)
      .then((setting) => setHeaderConfig(parseHeaderConfig(setting?.value)))
      .catch(() => setHeaderConfig(parseHeaderConfig()));
  }, [getSetting]);

  useEffect(() => {
    const handler = ({ setting }) => {
      if (setting?.key === HEADER_CONFIG_SETTING_KEY) setHeaderConfig(parseHeaderConfig(setting.value));
    };
    socket?.on(`company-${user?.companyId}-settings`, handler);
    return () => socket?.off(`company-${user?.companyId}-settings`, handler);
  }, [socket, user?.companyId]);

  const getRightAction = (key) => headerConfig.rightActions.find((action) => action.key === key);
  const getRightIcon = (key, fallback) => {
    const iconName = getRightAction(key)?.icon;
    if (!iconName) return fallback;
    return function RightIcon(props) {
      const FallbackComp = fallback;
      return <HeaderIcon icon={iconName} fallback={FallbackComp ? <FallbackComp {...props} /> : null} {...props} />;
    };
  };

  const [volume, setVolume] = useState(() => {
    const savedVolume = localStorage.getItem("volume");
    if (savedVolume === null) return 1;
    const parsedVolume = Number(savedVolume);
    return isNaN(parsedVolume) ? 1 : Math.max(0, Math.min(1, parsedVolume));
  });

  useEffect(() => {
    localStorage.setItem("volume", volume);
  }, [volume]);

  const [profileUrl, setProfileUrl] = useState(null);
  const userMenuRef = useRef(null);
  const avatarRef = useRef(null);
  const mobileActionsRef = useRef(null);
  const mobileActionsButtonRef = useRef(null);

  const isDarkMode = colorModeContext.mode === "dark";

  useEffect(() => {
    const companyId = user.companyId;
    const userId = user.id;
    if (companyId) {
      setProfileUrl(resolveProfileImageUrl(companyId, user.profileImage));

      const onCompanyAuthLayout = (data) => {
        if (data.user.id === +userId) {
          toastError("Sua conta foi acessada em outro computador.");
          setTimeout(() => {
            localStorage.clear();
            window.location.reload();
          }, 1000);
        }
      };

      socket.on(`company-${companyId}-auth`, onCompanyAuthLayout);

      socket.emit("userStatus");
      const interval = setInterval(() => {
        socket.emit("userStatus");
      }, 1000 * 60 * 5);

      socket.on("user-profile-update", (data) => {
        if (data.userId === userId) {
          const newProfileUrl = resolveProfileImageUrl(
            data.companyId,
            data.profileImage
          );
          setProfileUrl(newProfileUrl);
        }
      });

      return () => {
        socket.off(`company-${companyId}-auth`, onCompanyAuthLayout);
        clearInterval(interval);
        socket.off("user-profile-update");
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }

      if (
        mobileActionsRef.current &&
        !mobileActionsRef.current.contains(event.target) &&
        mobileActionsButtonRef.current &&
        !mobileActionsButtonRef.current.contains(event.target)
      ) {
        setMobileActionsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef, avatarRef]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  useEffect(() => {
    const fetchDiskUsage = async () => {
      if (user && user.profile === "admin") {
        try {
          const { data } = await api.get(
            `/companies/${user.companyId}/disk-usage`
          );
          setDiskUsage(data);
        } catch (error) {
          console.error("Erro ao buscar uso de disco:", error);
        }
      }
    };

    fetchDiskUsage();

    const interval = setInterval(fetchDiskUsage, 1000 * 60 * 60);

    return () => {
      clearInterval(interval);
    };
  }, [user]);

  useEffect(() => {
    let mounted = true;

    const fetchPlanFeatures = async () => {
      if (!user?.companyId) {
        if (mounted) {
          setPlanFeatures({});
          setPlanFeaturesLoaded(false);
        }
        return;
      }

      try {
        const { data } = await api.get(`/companies/listPlan/${user.companyId}`);
        if (!mounted) {
          return;
        }
        setPlanFeatures(data?.plan || {});
        setPlanFeaturesLoaded(true);
      } catch (_error) {
        if (!mounted) {
          return;
        }
        setPlanFeatures({});
        setPlanFeaturesLoaded(true);
      }
    };

    fetchPlanFeatures();

    const handlePlansChanged = () => {
      fetchPlanFeatures();
    };

    window.addEventListener("plans:changed", handlePlansChanged);

    return () => {
      mounted = false;
      window.removeEventListener("plans:changed", handlePlansChanged);
    };
  }, [user?.companyId]);

  useEffect(() => {
    if (!planFeaturesLoaded) {
      return;
    }

    const path = location.pathname || "";
    const checks = [
      { prefix: "/", key: "useDashboard", exact: true },
      { prefix: "/reports", key: "useRelatorio" },
      { prefix: "/tickets", key: "useTickets" },
      { prefix: "/support-tickets", key: "useSuporte" },
      { prefix: "/equipes", key: "useEquipe" },
      { prefix: "/emails", key: "useEmailModule" },
      { prefix: "/email-campaigns", key: "useEmailCampaign" },
      { prefix: "/portal-interno", key: "useInternalPortal" },
      { prefix: "/my-wallet", key: "useWallet" },
      { prefix: "/wallet-sector", key: "useWallet" },
      { prefix: "/wallet-management", key: "useWalletManagement" },
      { prefix: "/callcenter", key: "useCallcenter" },
      { prefix: "/callcenter-central", key: "useCallcenter" },
      { prefix: "/barber", key: "useBarber" },
      { prefix: "/pdv", key: "usePdv" },
      { prefix: "/comida-na-porta", key: "useComidaNaPorta" },
      { prefix: "/erpfone", keys: ["useTelefonia", "useErpPhone"] },
      { prefix: "/analytics-template-whats", keys: ["useDashboard", "useAnalyticsTemplateWhats"] },
      { prefix: "/whatsapp-templates", key: "useWhatsappTemplates" },
      { prefix: "/meta-ads", keys: ["useDashboard", "useMetaAds"] },
      { prefix: "/ai-agents", key: "useOpenAi" },
      { prefix: "/prompts", key: "useOpenAi" },
      { prefix: "/asaas-financial", key: "useAsaas" },
      { prefix: "/bancario-cartoes", key: "useBancarioCartoes" },
      { prefix: "/woocommerce", key: "useWooCommerce" },
      { prefix: "/meeting-rooms", key: "useMeetingRooms" },
      { prefix: "/files", key: "useFileManager" },
      { prefix: "/todolist", key: "useTasks" },
      { prefix: "/campaigns", key: "useCampaigns" },
      { prefix: "/voice-campaigns", key: "useVoiceCampaigns" },
      { prefix: "/group-campaigns", key: "useGroupCampaigns" },
      { prefix: "/schedules", key: "useSchedules" },
      { prefix: "/social-posts", key: "useRedeSocial" },
      { prefix: "/social-triggers", key: "useGatilho" },
      { prefix: "/agendacare", key: "useAgendacare" },
      { prefix: "/odontocare", key: "useOdontocare" },
      { prefix: "/petcare", key: "usePetcare" },
      { prefix: "/rotsa_360", key: "useRotaVendas360" },
      { prefix: "/chats", key: "useInternalChat" },
      { prefix: "/messages-api", key: "useExternalApi" },
      { prefix: "/kanban", key: "useKanban" },
      { prefix: "/queue-integration", key: "useIntegrations" },
      { prefix: "/voip", keys: ["useTelefonia", "useVoip"] }
    ];

    const blocked = checks.find((item) => {
      const matches = item.exact ? path === item.prefix : path.startsWith(item.prefix);
      if (!matches) {
        return false;
      }

      const keys = item.keys || (item.key ? [item.key] : []);
      return keys.some(key => !isPlanFeatureEnabled(planFeatures, key));
    });
    if (blocked) {
      history.replace("/");
      return;
    }

    const userPermissionChecks = [
      { prefix: "/tickets", key: "allowAttendance" },
      { prefix: "/quick-messages", key: "allowAttendance" },
      { prefix: "/support-tickets", key: "allowAttendance" },
      { prefix: "/emails", key: "allowEmail" },
      { prefix: "/kanban", key: "allowKanban" },
      { prefix: "/contacts", key: "allowContacts" },
      { prefix: "/tags", key: "allowTags" },
      { prefix: "/portal-interno", key: "allowInternalPortal" },
      { prefix: "/todolist", key: "allowTasks" },
      { prefix: "/my-wallet", key: "allowMyWallet" },
      { prefix: "/wallet-sector", key: "allowMyWallet" },
      { prefix: "/schedules", key: "allowSchedules" },
      { prefix: "/social-posts", key: "allowSchedules" },
      { prefix: "/social-triggers", key: "allowSchedules" },
      { prefix: "/agendacare", key: "allowAgendaCare" },
      { prefix: "/odontocare", key: "allowOdontoCare" },
      { prefix: "/petcare", key: "allowPetCare" },
      { prefix: "/callcenter-central", key: "allowCallcenterAdmin" },
      { prefix: "/callcenter", key: "allowCallcenterUser" },
      { prefix: "/barber", key: "allowBarber" },
      { prefix: "/chats", key: "allowInternalChat" },
      { prefix: "/pdv", key: "allowCashierPdv" },
      { prefix: "/comida-na-porta", key: "allowComidaNaPorta" },
      { prefix: "/erpfone", key: "allowErpPhone" },
      { prefix: "/campaigns", key: "allowCampaignsList" },
      { prefix: "/voice-campaigns", key: "allowCampaignsList" },
      { prefix: "/group-campaigns", key: "allowCampaignsList" },
      { prefix: "/email-campaigns", key: "allowEmailCampaigns" },
      { prefix: "/contact-lists", key: "allowContactLists" },
      { prefix: "/announcements", key: "allowAnnouncements" },
      { prefix: "/messages-api", key: "allowApi" },
      { prefix: "/knowledge-base", key: "allowKnowledgeBase" },
      { prefix: "/users/", key: "allowUserProfile" },
      { prefix: "/users", key: "allowUsers" },
      { prefix: "/queues", key: "allowQueues" },
      { prefix: "/equipes", key: "allowTeams" },
      { prefix: "/prompts", key: "allowTalkIa" },
      { prefix: "/ai-agents", key: "allowAiAgents" },
      { prefix: "/queue-integration", key: "allowIntegrations" },
      { prefix: "/asaas-financial", key: "allowAsaas" },
      { prefix: "/connections", key: "allowConnections" },
      { prefix: "/wallet-management", key: "allowWalletManagement" },
      { prefix: "/financeiro", key: "allowFinanceiro" },
      { prefix: "/settings", key: "allowSettings" }
    ];

    const hasHybridPermission = (key) => {
      const rawValue = String(user?.[key] || "").toLowerCase();
      if (rawValue === "disabled") return false;
      if (rawValue === "enabled") return true;

      const teamPermissions = user?.team?.permissions || {};
      const userPermissions = user?.permissions || {};
      const map = {
        allowAttendance: ["visualizar_tickets", "responder_tickets"],
        showDashboard: ["acessar_dashboard"],
        allowUsers: ["gerenciar_usuarios"],
        allowUserProfile: ["gerenciar_usuarios"],
        allowQueues: ["gerenciar_usuarios"],
        allowTeams: ["gerenciar_usuarios"],
        allowCampaigns: ["enviar_campanhas"],
        allowCampaignsList: ["enviar_campanhas"],
        allowFinanceiro: ["acessar_relatorios"]
      };
      const hybridKeys = map[key] || [];
      if (hybridKeys.length > 0) {
        return hybridKeys.some(
          permKey => Boolean(teamPermissions?.[permKey]) || Boolean(userPermissions?.[permKey])
        );
      }
      return rawValue !== "disabled";
    };

    const blockedByPermission = userPermissionChecks.find(
      (item) => path.startsWith(item.prefix) && !hasHybridPermission(item.key)
    );

    if (blockedByPermission) {
      history.replace("/");
    }
  }, [history, location.pathname, planFeatures, planFeaturesLoaded, user]);

  const toggleMenu = () => {
    setMobileActionsOpen(false);
    setMenuOpen(!menuOpen);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    setMenuOpen(false);
  };

  const handleOpenStatusModal = () => {
    setUserStatusModalOpen(true);
    setMenuOpen(false);
  };

  const handleClickLogout = () => {
    setMenuOpen(false);
    handleLogout();
  };

  const toggleMobileDrawer = () => {
    setMobileActionsOpen(false);
    setMenuOpen(false);
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const closeMobileDrawer = () => {
    setMobileDrawerOpen(false);
  };

  const toggleMobileActions = () => {
    setMenuOpen(false);
    setMobileActionsOpen((prev) => !prev);
  };

  const getConnectionStatusClass = () => {
    if (!connectionWarning) return "online";
    if (
      whatsApps.some(
        (whats) => whats.status === "DISCONNECTED" || whats.status === "TIMEOUT"
      )
    ) {
      return "offline";
    }
    return "warning";
  };

  const getConnectionStatusText = () => {
    const disconnectedCount = whatsApps.filter(
      (whats) =>
        whats.status === "DISCONNECTED" ||
        whats.status === "TIMEOUT" ||
        whats.status === "qrcode" ||
        whats.status === "PAIRING" ||
        whats.status === "OPENING"
    ).length;

    const connectedCount = whatsApps.length - disconnectedCount;

    if (!connectionWarning)
      return `${whatsApps.length}/${whatsApps.length} ${i18n.t(
        "connections.online"
      )}`;

    if (
      whatsApps.some(
        (whats) => whats.status === "DISCONNECTED" || whats.status === "TIMEOUT"
      )
    ) {
      return `${connectedCount}/${whatsApps.length} ${i18n.t(
        "connections.offline"
      )}`;
    }

    return `${connectedCount}/${whatsApps.length} ${i18n.t(
      "connections.warning"
    )}`;
  };

  const getQuotaBarColor = () => {
    if (!diskUsage || !diskUsage.diskQuota) return "success";

    const percentage = Math.round(
      (diskUsage.diskUsage / diskUsage.diskQuota) * 100
    );

    if (percentage > 85) return "error";
    if (percentage > 65) return "warning";
    return "success";
  };

  const getQuotaPercentage = () => {
    if (!diskUsage || !diskUsage.diskQuota) return 0;
    return Math.min(
      Math.round((diskUsage.diskUsage / diskUsage.diskQuota) * 100),
      100
    );
  };

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <GlobalSoftphoneProvider>
      <div className={`app-container ${isDarkMode ? "dark" : "light"}`}>
      <header className="navbar">
        <div className="navbar-container">
          <div className="navbar-left">
            <button
              className="mobile-menu-button"
              onClick={toggleMobileDrawer}
              aria-label="Menu"
            >
              <Menu className="icon" />
            </button>

            <div className="navbar-logo desktop-only">
              <Logo size="medium" />
            </div>

            <div className="navbar-menu">
              <div className="desktop-menu">
                <MainListItems />
              </div>
            </div>
          </div>

          <div className="navbar-actions">
            <div className="version-container"></div>

            {whatsApps.length > 0 && (
              <Tooltip
                title={
                  i18n.t("connections.title") + ": " + getConnectionStatusText()
                }
                placement="bottom"
                arrow
              >
                <div
                  className="connection-status-container"
                  aria-label={i18n.t("connections.title")}
                >
                  <div
                    className={`connection-status-indicator ${getConnectionStatusClass()}`}
                  ></div>
                  <span
                    className={`connection-status-text ${getConnectionStatusClass()}`}
                  >
                    {getConnectionStatusText()}
                  </span>
                </div>
              </Tooltip>
            )}

            {user.profile === "admin" &&
              diskUsage &&
              diskUsage.diskQuota > 0 && (
                <Tooltip
                  title={`Uso de disco: ${diskUsage.formattedDiskUsage} de ${diskUsage.formattedDiskQuota}`}
                  placement="bottom"
                  arrow
                >
                  <div className="quota-usage-container">
                    <span className="quota-usage-text">
                      <HardDrive
                        size={16}
                        className={`disk-icon ${getQuotaBarColor()}`}
                      />
                    </span>
                    <div className="quota-progress-root">
                      <div
                        className={`quota-progress-bar ${getQuotaBarColor()}`}
                        style={{ width: `${getQuotaPercentage()}%` }}
                      ></div>
                      <span className="quota-progress-text">
                        {getQuotaPercentage()}%
                      </span>
                    </div>
                  </div>
                </Tooltip>
              )}

            <div className="desktop-actions">
              {getRightAction("theme")?.visible && <div className="theme-toggle">
                <IconButtonWithTooltip
                  icon={getRightIcon("theme", isDarkMode ? Sun : Moon)}
                  tooltip={isDarkMode ? "Modo claro" : "Modo escuro"}
                  onClick={colorModeContext.toggleColorMode}
                  aria-label={isDarkMode ? "Modo claro" : "Modo escuro"}
                />
              </div>}

              {getRightAction("support")?.visible && <div className="support-toggle">
                <IconButtonWithTooltip
                  icon={getRightIcon("support", LifeBuoy)}
                  tooltip="Suporte"
                  onClick={() => history.push("/support-tickets")}
                  aria-label="Suporte"
                />
              </div>}

              {getRightAction("help")?.visible && <div className="help-assistant-toggle">
                <IconButtonWithTooltip icon={getRightIcon("help", CircleHelp)} tooltip="Ajuda IA" onClick={() => setHelpAssistantOpen(true)} aria-label="Abrir ajuda IA" />
              </div>}

              {getRightAction("clipboard")?.visible && <div className="clipboard-widget">
                <IconButtonWithTooltip
                  icon={getRightIcon("clipboard", ClipboardList)}
                  tooltip="Prancheta (Widgets)"
                  onClick={() => setClipboardDrawerOpen(true)}
                  aria-label="Abrir Prancheta"
                />
              </div>}

              {getRightAction("volume")?.visible && <div className="notifications-volume">
                <NotificationsVolume setVolume={setVolume} volume={volume} icon={getRightIcon("volume", Volume2)} />
              </div>}

              {getRightAction("notifications")?.visible && user.id && (
                <div className="notifications">
                  <NotificationsPopOver volume={volume} icon={getRightIcon("notifications", Bell)} />
                </div>
              )}

              {canAccessAnnouncements && getRightAction("announcements")?.visible && <div className="announcements">
                <AnnouncementsPopover icon={getRightIcon("announcements", Info)} />
              </div>}
            </div>

            <div className="mobile-actions">
              <button
                ref={mobileActionsButtonRef}
                className={`mobile-actions-button ${
                  mobileActionsOpen ? "open" : ""
                }`}
                onClick={toggleMobileActions}
                aria-label="Ações rápidas"
              >
                <SlidersHorizontal className="icon" />
              </button>
              {mobileActionsOpen && (
                <div className="mobile-actions-submenu" ref={mobileActionsRef}>
                  <div className="mobile-action-row">
                    <span className="mobile-action-label">
                      <Moon className="mobile-action-icon" />
                      Tema
                    </span>
                    <IconButtonWithTooltip
                      icon={isDarkMode ? Sun : Moon}
                      tooltip={isDarkMode ? "Modo claro" : "Modo escuro"}
                      onClick={colorModeContext.toggleColorMode}
                      aria-label={isDarkMode ? "Modo claro" : "Modo escuro"}
                    />
                  </div>
                  <div className="mobile-action-row">
                    <span className="mobile-action-label">
                      <Volume2 className="mobile-action-icon" />
                      Volume
                    </span>
                    <NotificationsVolume setVolume={setVolume} volume={volume} />
                  </div>
                  {user.id && (
                    <div className="mobile-action-row">
                      <span className="mobile-action-label">
                        <Bell className="mobile-action-icon" />
                        Notificações
                      </span>
                      <NotificationsPopOver volume={volume} />
                    </div>
                  )}
                  {getRightAction("support")?.visible && (
                    <button
                      type="button"
                      className="mobile-action-row mobile-action-row-button"
                      onClick={() => history.push("/support-tickets")}
                    >
                      <span className="mobile-action-label">
                        <LifeBuoy className="mobile-action-icon" />
                        Suporte
                      </span>
                    </button>
                  )}
                  {getRightAction("help")?.visible && (
                    <button
                      type="button"
                      className="mobile-action-row mobile-action-row-button"
                      onClick={() => {
                        setMobileActionsOpen(false);
                        setHelpAssistantOpen(true);
                      }}
                    >
                      <span className="mobile-action-label">
                        <CircleHelp className="mobile-action-icon" />
                        Ajuda IA
                      </span>
                    </button>
                  )}
                  {canAccessAnnouncements && (
                  <div className="mobile-action-row">
                    <span className="mobile-action-label">
                      <Megaphone className="mobile-action-icon" />
                      Avisos
                    </span>
                    <AnnouncementsPopover />
                  </div>
                  )}
                </div>
              )}
            </div>

            {getRightAction("profile")?.visible && <div className="user-profile">
              <Tooltip
                title={i18n.t("mainDrawer.appBar.user.profile") || "Perfil"}
                placement="bottom"
                arrow
              >
                <div
                  className="avatar-container"
                  ref={avatarRef}
                  onClick={toggleMenu}
                >
                  <div className={`status-badge ${user?.pausePolicyId ? "warning" : ""}`}>
                    <img 
                      alt="Lead Pro" 
                      className="avatar" 
                      src={profileUrl} 
                      onError={(e) => { e.target.onerror = null; e.target.src = "/avatar.svg"; }}
                    />
                  </div>
                </div>
              </Tooltip>
            </div>}

            {userModalOpen && (
              <Suspense fallback={null}>
                <UserModal
                  open={userModalOpen}
                  onClose={() => setUserModalOpen(false)}
                  onImageUpdate={(newProfileUrl) => {
                    if (
                      typeof window !== "undefined" &&
                      window.location.protocol === "https:" &&
                      /^http:\/\/(localhost|127\.0\.0\.1)/i.test(
                        String(newProfileUrl || "")
                      )
                    ) {
                      setProfileUrl(defaultAvatar);
                      return;
                    }
                    setProfileUrl(newProfileUrl || defaultAvatar);
                  }}
                  userId={user?.id}
                />
              </Suspense>
            )}

            {userStatusModalOpen && (
              <UserStatusModal
                open={userStatusModalOpen}
                onClose={() => setUserStatusModalOpen(false)}
              />
            )}

            {menuOpen && (
              <div className="user-menu" ref={userMenuRef}>
                <div className="user-menu-header">
                  <img
                    alt="Lead Pro"
                    src={profileUrl}
                    className="user-menu-avatar"
                    onError={(e) => { e.target.onerror = null; e.target.src = "/avatar.svg"; }}
                  />
                  <div className="user-info">
                    <p className="user-name">{user.name}</p>
                    <p className="user-email">{user.email}</p>
                  </div>
                </div>
                <div className="menu-divider"></div>
                {String(user?.allowUserProfile || "enabled") !== "disabled" && (
                  <button className="menu-item" onClick={handleOpenUserModal}>
                    <UserCircle className="menu-icon" />
                    <span>{i18n.t("mainDrawer.appBar.user.profile")}</span>
                  </button>
                )}
                <button className="menu-item" onClick={handleOpenStatusModal}>
                  <PauseCircle className="menu-icon" />
                  <div style={{ display: "flex", flexDirection: "column", flex: 1, textAlign: "left" }}>
                    <span>Status</span>
                    <span style={{ fontSize: "0.75rem", color: user?.pausePolicyId ? "#f59e0b" : "#10b981", fontWeight: 600 }}>
                      {user?.pausePolicyId ? (user?.pausePolicy?.name || "Em Pausa") : "Online"}
                    </span>
                  </div>
                </button>
                <button className="menu-item" onClick={handleClickLogout}>
                  <LogOut className="menu-icon" />
                  <span>{i18n.t("mainDrawer.appBar.user.logout")}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Drawer mobile */}
      {mobileDrawerOpen && (
        <div className="drawer-backdrop" onClick={toggleMobileDrawer}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div className="drawer-logo">
                <Logo size="medium" />
              </div>
              <button
                className="close-drawer-button"
                onClick={toggleMobileDrawer}
              >
                <X className="icon" />
              </button>
            </div>

            <div className="drawer-user">
              <div className="drawer-user-avatar">
                <img 
                  alt="Lead Pro" 
                  src={profileUrl} 
                  onError={(e) => { e.target.onerror = null; e.target.src = "/avatar.svg"; }}
                />
              </div>
              <div className="drawer-user-info">
                <p className="drawer-user-name">{user.name}</p>
                <p className="drawer-user-email">{user.email}</p>
              </div>
            </div>

            <div className="menu-divider"></div>

            <div className="drawer-container">
              <MainListItems isMobile={true} closeDrawer={closeMobileDrawer} />
            </div>

            <div className="drawer-footer">
              <button
                className="drawer-footer-button"
                onClick={colorModeContext.toggleColorMode}
              >
                {isDarkMode ? (
                  <>
                    <Sun className="drawer-footer-icon" />
                    <span>{i18n.t("mainDrawer.appBar.theme.light")}</span>
                  </>
                ) : (
                  <>
                    <Moon className="drawer-footer-icon" />
                    <span>{i18n.t("mainDrawer.appBar.theme.dark")}</span>
                  </>
                )}
              </button>

              <button
                className="drawer-footer-button"
                onClick={handleClickLogout}
              >
                <LogOut className="drawer-footer-icon" />
                <span>{i18n.t("mainDrawer.appBar.user.logout")}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="main-content">
        <div className="content-container">{children}</div>
      </main>
      <Suspense fallback={null}>
        {getRightAction("softphone")?.visible && <GlobalSoftphoneWidget />}

        {/* Widgets Flutuantes */}
        {activeWidgets?.dollar && (
          <FloatingWidget
            id="dollar"
            title="Cotação Moedas"
            icon={<DollarSign size={18} color="#ffffff" />}
          >
            <DollarWidget />
          </FloatingWidget>
        )}

        {activeWidgets?.calculator && (
          <FloatingWidget
            id="calculator"
            title="Calculadora"
            icon={<Calculator size={18} color="#ffffff" />}
          >
            <CalculatorWidget />
          </FloatingWidget>
        )}

        {activeWidgets?.notepad && (
          <FloatingWidget
            id="notepad"
            title="Bloco de Notas"
            icon={<FileText size={18} color="#ffffff" />}
          >
            <NotepadWidget />
          </FloatingWidget>
        )}

        {activeWidgets?.weather && (
          <FloatingWidget
            id="weather"
            title="Previsão do Tempo"
            icon={<CloudSun size={18} color="#ffffff" />}
          >
            <WeatherWidget />
          </FloatingWidget>
        )}

        {/* Drawer da Prancheta */}
        {clipboardDrawerOpen && (
          <ClipboardDrawer
            open={clipboardDrawerOpen}
            onClose={() => setClipboardDrawerOpen(false)}
          />
        )}
        {helpAssistantOpen && (
          <HelpAssistantModal open={helpAssistantOpen} onClose={() => setHelpAssistantOpen(false)} pathname={location.pathname} />
        )}
      </Suspense>
    </div>
  </GlobalSoftphoneProvider>
);
};

export default LoggedInLayout;
